import { message, notification } from 'antd';
import { logGameError } from './gameErrorHandler';

// 소켓 연결 관련 오류 처리
export const handleSocketError = (error, context = {}) => {
    console.error('Socket Error:', error, context);
    
    switch (error.type) {
        case 'connection_failed':
            message.error('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
            break;
            
        case 'connection_timeout':
            message.error('서버 연결 시간이 초과되었습니다. 다시 시도해주세요.');
            break;
            
        case 'disconnected':
            notification.warning({
                message: '서버 연결이 끊어졌습니다',
                description: '자동으로 재연결을 시도합니다...',
                duration: 3
            });
            break;
            
        case 'reconnect_failed':
            notification.error({
                message: '서버 재연결 실패',
                description: '페이지를 새로고침하거나 잠시 후 다시 시도해주세요.',
                duration: 0, // 수동으로 닫을 때까지 유지
                key: 'reconnect_failed'
            });
            break;
            
        case 'room_join_failed':
            message.error(`방 참가에 실패했습니다: ${error.message}`);
            break;
            
        case 'room_not_found':
            message.error('존재하지 않는 방입니다. 방 코드를 확인해주세요.');
            break;
            
        case 'room_full':
            message.error('방이 가득 찼습니다. 다른 방을 찾아보세요.');
            break;
            
        case 'invalid_action':
            message.warning('현재 상황에서는 할 수 없는 행동입니다.');
            break;
            
        case 'permission_denied':
            message.error('권한이 없습니다.');
            break;
            
        case 'server_error':
            message.error('서버에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
            break;
            
        case 'version_mismatch':
            notification.error({
                message: '클라이언트 버전 불일치',
                description: '페이지를 새로고침해주세요.',
                duration: 0,
                key: 'version_mismatch'
            });
            break;
            
        default:
            if (error.message) {
                message.error(error.message);
            } else {
                message.error('알 수 없는 오류가 발생했습니다.');
            }
    }
    
    // 오류 로깅 (게임 에러는 gameErrorHandler에서 처리하므로 소켓 에러만 로깅)
    if (error.type !== 'game_error') {
        logGameError(error, {
            ...context,
            errorType: 'socket_error',
            timestamp: new Date().toISOString()
        });
    }
};

// 소켓 상태별 오류 처리
export const handleSocketStateError = (state, error) => {
    switch (state) {
        case 'connecting':
            console.log('서버에 연결 중...');
            break;
            
        case 'connect_error':
            handleSocketError({
                type: 'connection_failed',
                message: error?.message || '연결 실패'
            });
            break;
            
        case 'disconnect':
            if (error !== 'io client disconnect') { // 의도적인 연결 해제가 아닌 경우
                handleSocketError({
                    type: 'disconnected',
                    message: '서버 연결이 끊어졌습니다'
                });
            }
            break;
            
        case 'reconnect_attempt':
            console.log('재연결 시도 중...');
            break;
            
        case 'reconnect_error':
            console.error('재연결 실패:', error);
            break;
            
        case 'reconnect_failed':
            handleSocketError({
                type: 'reconnect_failed',
                message: '서버 재연결에 실패했습니다'
            });
            break;
            
        case 'reconnect':
            notification.success({
                message: '서버에 재연결되었습니다',
                duration: 2,
                key: 'reconnect_success'
            });
            // 재연결 실패 알림 제거
            try {
                notification.destroy('reconnect_failed');
            } catch (e) {
                console.warn('Failed to close notification:', e);
            }
            break;
    }
};

// 네트워크 상태 확인 및 처리
export const handleNetworkStatus = () => {
    const updateOnlineStatus = () => {
        if (navigator.onLine) {
            notification.success({
                message: '네트워크가 복구되었습니다',
                duration: 2,
                key: 'network_restored'
            });
            try {
                notification.destroy('network_offline');
            } catch (e) {
                console.warn('Failed to close notification:', e);
            }
        } else {
            notification.error({
                message: '네트워크 연결이 끊어졌습니다',
                description: '인터넷 연결을 확인해주세요.',
                duration: 0,
                key: 'network_offline'
            });
        }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // 정리 함수 반환
    return () => {
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
    };
};

// 소켓 이벤트 오류 래퍼
export const wrapSocketEvent = (eventName, handler, errorContext = {}) => {
    return (...args) => {
        try {
            return handler(...args);
        } catch (error) {
            handleSocketError({
                type: 'event_handler_error',
                message: `${eventName} 이벤트 처리 중 오류 발생`,
                originalError: error
            }, {
                eventName,
                ...errorContext
            });
        }
    };
};

// 소켓 액션 결과 처리
export const handleSocketActionResult = (action, result) => {
    if (result?.error) {
        handleSocketError({
            type: 'action_error',
            message: result.error,
            action
        });
        return false;
    }
    
    if (result?.ok === false) {
        handleSocketError({
            type: 'action_failed',
            message: `${action} 실행에 실패했습니다`,
            action
        });
        return false;
    }
    
    return true;
};

// 재연결 로직
export const createReconnectHandler = (socketService, maxAttempts = 5) => {
    let reconnectAttempts = 0;
    let reconnectTimer = null;
    
    const attemptReconnect = () => {
        if (reconnectAttempts >= maxAttempts) {
            handleSocketError({
                type: 'reconnect_failed',
                message: `${maxAttempts}번의 재연결 시도가 모두 실패했습니다`
            });
            return;
        }
        
        reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 10000); // 지수 백오프
        
        console.log(`재연결 시도 ${reconnectAttempts}/${maxAttempts} (${delay}ms 후)`);
        
        reconnectTimer = setTimeout(() => {
            try {
                socketService.connect();
            } catch (error) {
                console.error('재연결 시도 실패:', error);
                attemptReconnect();
            }
        }, delay);
    };
    
    const resetReconnectAttempts = () => {
        reconnectAttempts = 0;
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    };
    
    return {
        attemptReconnect,
        resetReconnectAttempts,
        getReconnectAttempts: () => reconnectAttempts
    };
};